import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from '../contexts/Web3Context';
import { ROUTER_ADDRESS, PUZO_ADDRESS, USDT_ADDRESS } from '../config/constants';
import { useBlockchainData } from '../hooks/useBlockchainData';
import ROUTER_ABI from '../abis/router.json';
import ERC20_ABI from '../abis/erc20.json';

const Swap: React.FC = () => {
  const { provider, signer, account, isConnected, connectWallet } = useWeb3();
  const { puzoUsdtPool } = useBlockchainData(provider);
  
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fromToken, setFromToken] = useState(PUZO_ADDRESS);
  const [toToken, setToToken] = useState(USDT_ADDRESS);
  const [fromBalance, setFromBalance] = useState('0');
  const [toBalance, setToBalance] = useState('0');
  const [approved, setApproved] = useState(false);

  const fromTokenSymbol = fromToken === PUZO_ADDRESS ? 'PUZO' : 'USDT';
  const toTokenSymbol = toToken === PUZO_ADDRESS ? 'PUZO' : 'USDT';

  useEffect(() => {
    const loadBalances = async () => {
      if (!provider || !account) return;

      try {
        const fromContract = new ethers.Contract(fromToken, ERC20_ABI, provider);
        const toContract = new ethers.Contract(toToken, ERC20_ABI, provider);
        const fromAllowance = await fromContract.allowance(account, ROUTER_ADDRESS);

        const [fromBal, toBal] = await Promise.all([
          fromContract.balanceOf(account),
          toContract.balanceOf(account)
        ]);

        setFromBalance(ethers.utils.formatEther(fromBal));
        setToBalance(ethers.utils.formatEther(toBal));
        setApproved(fromAllowance.gt(ethers.utils.parseEther('1000')));
      } catch (error) {
        console.error('Error loading balances:', error);
      }
    };

    loadBalances();
  }, [provider, account, fromToken, toToken]);

  // Calculate output amount based on pool reserves
  useEffect(() => {
    if (fromAmount && puzoUsdtPool) {
      if (fromToken === PUZO_ADDRESS && toToken === USDT_ADDRESS) {
        const inputAmount = parseFloat(fromAmount);
        const reserveIn = parseFloat(puzoUsdtPool.reserve0);
        const reserveOut = parseFloat(puzoUsdtPool.reserve1);
        const amountOut = (inputAmount * reserveOut) / (reserveIn + inputAmount);
        setToAmount(amountOut.toFixed(6));
      } else if (fromToken === USDT_ADDRESS && toToken === PUZO_ADDRESS) {
        const inputAmount = parseFloat(fromAmount);
        const reserveIn = parseFloat(puzoUsdtPool.reserve1);
        const reserveOut = parseFloat(puzoUsdtPool.reserve0);
        const amountOut = (inputAmount * reserveOut) / (reserveIn + inputAmount);
        setToAmount(amountOut.toFixed(6));
      }
    } else {
      setToAmount('');
    }
  }, [fromAmount, fromToken, toToken, puzoUsdtPool]);

  const switchTokens = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setFromAmount(toAmount);
    setToAmount(fromAmount);
  };

  const approveToken = async () => {
    if (!signer) return;

    try {
      setLoading(true);
      setError('');

      const tokenContract = new ethers.Contract(fromToken, ERC20_ABI, signer);
      const tx = await tokenContract.approve(ROUTER_ADDRESS, ethers.constants.MaxUint256);
      await tx.wait();
      setApproved(true);
    } catch (error: any) {
      setError('Approve failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSwap = async () => {
    if (!isConnected || !signer || !account) {
      await connectWallet();
      return;
    }

    if (!fromAmount || parseFloat(fromAmount) <= 0) {
      setError('Please enter valid amount');
      return;
    }

    if (parseFloat(fromAmount) > parseFloat(fromBalance)) {
      setError('Insufficient balance');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const routerContract = new ethers.Contract(ROUTER_ADDRESS, ROUTER_ABI, signer);
      const amountIn = ethers.utils.parseEther(fromAmount);
      const path = [fromToken, toToken];
      const deadline = Math.floor(Date.now() / 1000) + 60 * 20;

      const tx = await routerContract.swapExactTokensForTokens(
        amountIn,
        0,
        path,
        account,
        deadline,
        { gasLimit: 300000 }
      );

      await tx.wait();
      setFromAmount('');
      setToAmount('');
      setError('Swap completed successfully');
    } catch (error: any) {
      setError('Swap failed');
    } finally {
      setLoading(false);
    }
  };

  const formatInput = (value: string) => {
    return value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
  };

  const formatBalance = (balance: string) => {
    return parseFloat(balance).toLocaleString('en-US', {
      maximumFractionDigits: 6
    });
  };

  const canSwap = isConnected && 
    fromAmount && 
    toAmount && 
    parseFloat(fromAmount) > 0 && 
    parseFloat(toAmount) > 0 &&
    parseFloat(fromAmount) <= parseFloat(fromBalance) &&
    approved;

  return (
    <div>
      {error && (
        <div className={`system-alert ${error.includes('successfully') ? 'success' : 'error'}`}>
          {error}
        </div>
      )}

      <div className="interface-panel">
        <div className="panel-header">
          <div className="panel-title">Exchange</div>
        </div>
        <div className="panel-content">
          <div className="input-group">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
              <div className="input-label">From</div>
              {isConnected && (
                <div style={{fontSize: '0.7rem', color: '#888'}}>
                  Balance: {formatBalance(fromBalance)}
                </div>
              )}
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <input
                type="text"
                className="quantum-input"
                value={fromAmount}
                onChange={(e) => setFromAmount(formatInput(e.target.value))}
                placeholder="0.0"
                disabled={loading}
                style={{flex: 1}}
              />
              <div style={{
                padding: '8px 12px',
                background: 'rgba(10, 10, 15, 0.8)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                fontSize: '0.8rem',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                {fromTokenSymbol}
              </div>
            </div>
          </div>

          {/* Switch Button */}
          <div style={{display: 'flex', justifyContent: 'center', margin: '10px 0'}}>
            <button 
              onClick={switchTokens}
              style={{
                background: 'transparent',
                border: '1px solid var(--border)',
                color: 'var(--primary)',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem'
              }}
            >
              ⇅
            </button>
          </div>

          <div className="input-group">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
              <div className="input-label">To</div>
              {isConnected && (
                <div style={{fontSize: '0.7rem', color: '#888'}}>
                  Balance: {formatBalance(toBalance)}
                </div>
              )}
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <input
                type="text"
                className="quantum-input"
                value={toAmount}
                readOnly
                placeholder="0.0"
                style={{flex: 1}}
              />
              <div style={{
                padding: '8px 12px',
                background: 'rgba(10, 10, 15, 0.8)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                fontSize: '0.8rem',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                {toTokenSymbol}
              </div>
            </div>
          </div>

          {isConnected && !approved && (
            <button 
              className="quantum-btn mb-16"
              onClick={approveToken}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="quantum-loading"></span>
                  APPROVING
                </>
              ) : (
                `APPROVE ${fromTokenSymbol}`
              )}
            </button>
          )}

          {!isConnected ? (
            <button className="quantum-btn primary" onClick={connectWallet}>
              CONNECT WALLET
            </button>
          ) : (
            <button 
              className="quantum-btn primary" 
              onClick={handleSwap}
              disabled={loading || !canSwap}
            >
              {loading ? (
                <>
                  <span className="quantum-loading"></span>
                  SWAPPING
                </>
              ) : (
                `SWAP ${fromTokenSymbol} → ${toTokenSymbol}`
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Swap;
